<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="savePage">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ isEdit ? $t('Seite bearbeiten') : $t('Seite anlegen') }}</h1>
                    </div>
                    <div class="inline-flex items-center">
                        <div
                            v-if="isEdit"
                            ref="headerActionMenuWrapper"
                            class="relative inline-flex items-stretch mr-0"
                        >
                            <button class="btn-stage-primary !rounded-r-none !pr-3.5 inline-flex items-center gap-1.5" type="submit">
                                <span>{{ $t('Save') }}</span>
                                <span
                                    class="h-2 w-2 flex-shrink-0 rounded-full"
                                    :class="[publicationStateDotClass, showPublicationStateDot ? '' : 'opacity-0']"
                                    :title="publicationStateLabel"
                                    :aria-label="publicationStateLabel"
                                ></span>
                            </button>
                            <button
                                ref="headerActionMenuTrigger"
                                class="btn-stage-primary !rounded-l-none border-l border-l-[rgba(255,255,255,0.16)] min-w-[2.25rem] !px-2 inline-flex items-center justify-center"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :aria-expanded="headerActionMenuOpen ? 'true' : 'false'"
                                @click.stop="toggleHeaderActionMenu"
                            >
                                <svg-vue
                                    class="h-4 w-4"
                                    icon="font-awesome.regular.ellipsis-vertical"
                                ></svg-vue>
                            </button>
                            <div
                                v-show="headerActionMenuOpen"
                                ref="headerActionMenu"
                                class="absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                            >
                                <button
                                    class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                    type="button"
                                    @click.stop="runHeaderAction('publish')"
                                >
                                    {{ $t('Veröffentlichen') }}
                                </button>
                                <button
                                    v-if="page.published_at"
                                    class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                    type="button"
                                    @click.stop="runHeaderAction('reset')"
                                >
                                    {{ $t('Zurücksetzen (letzte Veröffentlichung)') }}
                                </button>
                                <button
                                    class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                    type="button"
                                    @click.stop="runHeaderAction('preview')"
                                >
                                    {{ $t('Vorschau') }}
                                </button>
                                <button
                                    class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                    type="button"
                                    @click.stop="runHeaderAction('cancel')"
                                >
                                    {{ $t('Abbrechen') }}
                                </button>
                                <button
                                    class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                    type="button"
                                    @click.stop="runHeaderAction('delete')"
                                >
                                    {{ $t('Seite löschen') }}
                                </button>
                            </div>
                        </div>
                        <button v-else class="btn-stage-primary" type="submit">
                            {{ $t('Seite anlegen') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="space-y-6">
                    <app-panel>
                        <template #header>
                            <template v-if="isEdit">
                                <div class="flex flex-wrap items-center gap-x-3 gap-y-2 text-base text-gray-600">
                                    <router-link
                                        to="/workspace/cms/pages"
                                        class="inline-flex items-center text-base font-base text-gray-600 transition-colors hover:text-gray-900"
                                        :aria-label="$t('Seitenübersicht')"
                                        :title="$t('Seitenübersicht')"
                                    >
                                        <svg-vue
                                            icon="font-awesome.regular.grid-2"
                                            class="h-4 w-4 flex-shrink-0 text-gray-400"
                                        />
                                    </router-link>
                                    <span class="text-gray-300">/</span>
                                    <span class="inline-flex items-center gap-2 text-base font-base text-gray-900">
                                        <svg-vue
                                            icon="font-awesome.regular.file"
                                            class="h-4 w-4 flex-shrink-0 text-gray-500"
                                        />
                                        <span>{{ page.name || $t('Seite bearbeiten') }}</span>
                                        <span
                                            class="h-2 w-2 flex-shrink-0 rounded-full"
                                            :class="contentStatusDotClass(page)"
                                            :title="contentStatusLabel(page)"
                                            :aria-label="contentStatusLabel(page)"
                                        ></span>
                                    </span>
                                </div>
                            </template>
                        </template>
                            <div class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-6 items-end">
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Name') }}</label>
                                    <input v-model="page.name" class="stage-filter-input form-input block w-full text-sm" required>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Template') }}</label>
                                    <select v-model="page.cms_template_id" class="stage-filter-input form-select block w-full text-sm" required>
                                        <option v-for="template in templateOptions" :key="template.id" :value="template.id">
                                            {{ template.name }}
                                        </option>
                                    </select>
                                </div>
                                <div class="flex min-h-10 items-center pt-7">
                                    <input-switch
                                        id="cms-page-active"
                                        v-model="page.is_active"
                                        :disabled-label="$t('Aktiviert')"
                                        :enabled-label="$t('Aktiviert')"
                                    />
                                </div>
                            </div>
                    </app-panel>

                    <app-panel :title="$t('Sprachvarianten')" body-class="!p-0">
                            <div v-if="page.translations.length > 0" class="flex flex-col">
                                <div class="flex flex-wrap items-center gap-0 border-b border-gray-100 px-5">
                                    <button
                                        v-for="(translation, index) in page.translations"
                                        :key="translation.id || ('new-' + index)"
                                        type="button"
                                        class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                        :class="activeTranslationIndex === index ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                        @click="activeTranslationIndex = index"
                                    >
                                        {{ translationLabel(translation, index) }}
                                    </button>
                                </div>

                                <div v-if="activeTranslation" class="cms-translation-content">
                                    <div class="cms-translation-meta">
                                        <div class="flex flex-col gap-6 xl:flex-row xl:items-center">
                                            <div class="flex-1 min-w-0">
                                                <label class="stage-filter-label block mb-2">{{ $t('Titel') }}</label>
                                                <input v-model="activeTranslation.title" class="stage-filter-input form-input block w-full text-sm">
                                            </div>
                                            <div class="shrink-0 flex items-center">
                                                <input-switch
                                                    :id="'cms-page-translation-active-' + activeTranslationIndex"
                                                    v-model="activeTranslation.is_active"
                                                    :disabled-label="$t('Aktiviert')"
                                                    :enabled-label="$t('Aktiviert')"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="cms-translation-accordion">
                                        <button
                                            class="cms-translation-accordion-trigger"
                                            type="button"
                                            @click="metaTagsAccordionOpen = !metaTagsAccordionOpen"
                                        >
                                            <span>{{ $t('Metatags') }}</span>
                                            <svg-vue
                                                class="h-[0.875rem] w-[0.875rem] text-gray-400 transition-transform duration-150"
                                                :class="metaTagsAccordionOpen ? 'rotate-180' : ''"
                                                icon="font-awesome.regular.angle-down"
                                            ></svg-vue>
                                        </button>
                                        <div v-if="metaTagsAccordionOpen" class="cms-translation-accordion-body">
                                            <div class="cms-translation-meta">
                                                <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                                    <div>
                                                        <label class="stage-filter-label block mb-2">{{ $t('SEO-Titel') }}</label>
                                                        <input v-model="activeTranslation.seo_title" class="stage-filter-input form-input block w-full text-sm">
                                                    </div>
                                                    <div>
                                                        <label class="stage-filter-label block mb-2">{{ $t('Robots') }}</label>
                                                        <select v-model="activeTranslation.seo_robots" class="stage-filter-input form-select block w-full text-sm">
                                                            <option v-for="option in seoRobotsOptions" :key="option.value" :value="option.value">
                                                                {{ option.label }}
                                                            </option>
                                                        </select>
                                                    </div>
                                                    <div class="xl:col-span-2">
                                                        <label class="stage-filter-label block mb-2">{{ $t('Meta-Beschreibung') }}</label>
                                                        <textarea v-model="activeTranslation.seo_description" rows="3" class="stage-filter-input form-textarea block w-full text-sm"></textarea>
                                                    </div>
                                                    <div class="xl:col-span-2">
                                                        <label class="stage-filter-label block mb-2">{{ $t('Canonical URL') }}</label>
                                                        <input
                                                            v-model="activeTranslation.seo_canonical_url"
                                                            class="stage-filter-input form-input block w-full text-sm"
                                                            placeholder="https://www.beispiel.de/impressum"
                                                        >
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="cms-translation-code-head flex items-center justify-between gap-4">
                                                <span>{{ $t('Erweiterte Metatags') }}</span>
                                                <button class="btn-stage-secondary text-xs" type="button" @click="addMetaTag">
                                                    {{ $t('Metatag hinzufügen') }}
                                                </button>
                                            </div>
                                            <div class="p-0">
                                                <div v-if="activeTranslation.meta_tags.length > 0" class="overflow-auto">
                                                    <table class="table-listview table-listview--zebra cms-meta-tag-table w-full">
                                                        <thead>
                                                        <tr>
                                                            <th class="table-listview-l-th whitespace-nowrap w-52">{{ $t('Typ') }}</th>
                                                            <th class="table-listview-l-th whitespace-nowrap w-64">{{ $t('Schlüssel') }}</th>
                                                            <th class="table-listview-l-th">{{ $t('Inhalt') }}</th>
                                                            <th class="table-listview-l-th whitespace-nowrap w-28">{{ $t('Status') }}</th>
                                                            <th class="table-listview-l-th whitespace-nowrap w-28">
                                                                <div class="w-full text-right">{{ $t('Aktionen') }}</div>
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody class="bg-white">
                                                        <tr v-for="(metaTag, index) in activeTranslation.meta_tags" :key="'meta-tag-' + activeTranslation.cms_language_id + '-' + index">
                                                            <td class="table-listview-l-td align-middle">
                                                                <select v-model="metaTag.attribute_type" class="stage-filter-input form-select block w-full text-sm">
                                                                    <option v-for="option in metaTagAttributeTypeOptions" :key="option.value" :value="option.value">
                                                                        {{ option.label }}
                                                                    </option>
                                                                </select>
                                                            </td>
                                                            <td class="table-listview-l-td align-middle">
                                                                <input v-model="metaTag.attribute_key" class="stage-filter-input form-input block w-full text-sm" placeholder="og:title">
                                                            </td>
                                                            <td class="table-listview-l-td align-middle">
                                                                <input v-model="metaTag.content" class="stage-filter-input form-input block w-full text-sm" placeholder="Inhalt des Metatags">
                                                            </td>
                                                            <td class="table-listview-l-td align-middle whitespace-nowrap text-left">
                                                                <label class="inline-flex items-center text-sm text-gray-700">
                                                                    <input
                                                                        :id="'cms-page-meta-tag-' + activeTranslation.cms_language_id + '-' + index"
                                                                        v-model="metaTag.is_active"
                                                                        class="form-checkbox rounded border-gray-300 text-gray-800 focus:ring-gray-300"
                                                                        type="checkbox"
                                                                    >
                                                                </label>
                                                            </td>
                                                            <td class="table-listview-l-td align-middle whitespace-nowrap text-right">
                                                                <div class="inline-flex w-full items-center justify-end">
                                                                    <button
                                                                        class="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 focus:outline-none"
                                                                        type="button"
                                                                        :title="$t('Aktionen')"
                                                                        :aria-label="$t('Aktionen')"
                                                                        :data-page-meta-tag-action-trigger="index"
                                                                        @click.stop="toggleMetaTagActionMenu(index, metaTag, $event)"
                                                                    >
                                                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div v-else class="p-6 text-sm text-gray-500">
                                                    {{ $t('Noch keine individuellen Metatags vorhanden.') }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="cms-translation-code-head cms-translation-code-head--flush-top flex items-center justify-between gap-4">
                                        <span>{{ $t('HTML') }}</span>
                                        <button
                                            class="inline-flex cursor-pointer items-center justify-center text-gray-400 transition hover:text-gray-700"
                                            type="button"
                                            :title="$t('Datei einfügen')"
                                            :aria-label="$t('Datei einfügen')"
                                            @click="openContentHtmlMediaDrawer"
                                        >
                                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.image"></svg-vue>
                                        </button>
                                    </div>
                                    <div class="cms-translation-html-editor">
                                        <cms-html-code-editor ref="pageContentHtmlEditor" v-model="activeTranslation.content_html" :rows="24" variant="vault" fill />
                                    </div>
                                </div>
                            </div>

                            <div v-else class="p-6 text-sm text-gray-500">
                                {{ $t('Noch keine Sprachvarianten vorhanden.') }}
                            </div>
                        <template v-if="activeTranslation" #footer>
                            <div class="text-sm text-gray-500">
                                {{ activeTranslation.routes_count || 0 }} Domain-Zuordnungen
                            </div>
                        </template>
                    </app-panel>

                    <cms-slot-table
                        v-for="slotGroup in page.slot_groups"
                        v-if="isEdit"
                        :key="slotGroup.slot"
                        :group="slotGroup"
                        :context-id="page.id"
                        context-type="page"
                        :module-type-options="moduleTypeOptions"
                        :navigation-context="slotNavigationContext"
                        @changed="getPage"
                        @reordered="applySlotReorder"
                    />

                    <app-panel :title="$t('Code')" panel-class="self-start" body-class="!p-0 h-[34rem] overflow-hidden">
                            <div class="flex h-full flex-col">
                                <div class="flex flex-wrap items-center gap-0 border-b border-gray-100 bg-white px-5">
                                    <button
                                        v-for="tab in codeTabs"
                                        :key="tab.key"
                                        type="button"
                                        class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                        :class="activeCodeTab === tab.key ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                        @click="activeCodeTab = tab.key"
                                    >
                                        {{ tab.label }}
                                    </button>
                                </div>

                                <div v-show="activeCodeTab === 'page_css'" class="h-full min-h-0 flex-1">
                                    <html-code-editor v-model="page.page_css" :rows="24" variant="vault" fill />
                                </div>
                                <div v-show="activeCodeTab === 'page_js_footer'" class="h-full min-h-0 flex-1">
                                    <html-code-editor v-model="page.page_js_footer" :rows="24" variant="vault" fill />
                                </div>
                            </div>
                    </app-panel>

                </div>
            </div>
        </form>
        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :headline-id="'cms-page-confirm-modal-headline'"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />

        <div
            v-if="metaTagActionMenu.index !== null"
            ref="metaTagActionMenu"
            class="fixed z-[9999] w-52 rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
            :style="metaTagActionMenuStyle"
            @click.stop
        >
            <button
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                type="button"
                @click.stop="removeActionMenuMetaTag"
            >
                {{ $t('Metatag entfernen') }}
            </button>
        </div>
    </main>
</template>

<script>
import HtmlCodeEditor from "@/components/forms/html-code-editor";
import CmsHtmlCodeEditor from "@/components/forms/cms-html-code-editor";
import CmsSlotTable from "@/components/cms/cms-slot-table";
import ConfirmDialog from "@/components/modals/confirm-dialog";

export default {
    name: "cms-page-editor",
    components: {
        CmsSlotTable,
        HtmlCodeEditor,
        CmsHtmlCodeEditor,
        ConfirmDialog,
    },
    data() {
        return {
            loading: false,
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            },
            metaTagActionMenu: {
                index: null,
                top: 0,
                left: 0,
                openUpward: false,
            },
            headerActionMenuOpen: false,
            pageStateResolved: false,
            metaTagsAccordionOpen: false,
            activeTranslationIndex: 0,
            activeCodeTab: 'page_css',
            templateOptions: [],
            languageOptions: [],
            moduleTypeOptions: [],
            defaultLanguageId: null,
            page: {
                id: null,
                cms_template_id: null,
                key: '',
                name: '',
                page_css: '',
                page_js_footer: '',
                is_active: true,
                has_unpublished_changes: false,
                published_at: null,
                slot_groups: [],
                translations: [],
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
        codeTabs() {
            return [
                {key: 'page_css', label: 'CSS'},
                {key: 'page_js_footer', label: 'JS (Footer)'},
            ];
        },
        seoRobotsOptions() {
            return [
                {value: 'index,follow', label: 'index,follow'},
                {value: 'noindex,follow', label: 'noindex,follow'},
                {value: 'noindex,nofollow', label: 'noindex,nofollow'},
            ];
        },
        metaTagAttributeTypeOptions() {
            return [
                {value: 'name', label: this.$t('Name')},
                {value: 'property', label: this.$t('Property')},
            ];
        },
        activeTranslation() {
            return this.page.translations[this.activeTranslationIndex] || null;
        },
        slotNavigationContext() {
            return {
                originPath: this.$route.fullPath,
                modulePath: [],
            };
        },
        publicationStateLabel() {
            if (!this.page.is_active) {
                return this.$t('Disabled');
            }

            if (!this.page.published_at) {
                return this.$t('Noch nicht veröffentlicht');
            }

            if (this.page.has_unpublished_changes) {
                return this.$t('Unveröffentlichte Änderungen');
            }

            return this.$t('Veröffentlicht');
        },
        publicationStateClass() {
            if (!this.page.is_active) {
                return 'text-red-600';
            }

            if (!this.page.published_at) {
                return 'text-gray-500';
            }

            return this.page.has_unpublished_changes ? 'text-amber-600' : 'text-green-600';
        },
        publicationStateDotClass() {
            return this.contentStatusDotClass(this.page);
        },
        showPublicationStateDot() {
            return !this.isEdit || this.pageStateResolved;
        },
        metaTagActionMenuStyle() {
            const top = this.metaTagActionMenu.openUpward
                ? Math.max(this.metaTagActionMenu.top - 48, 12)
                : this.metaTagActionMenu.top;

            return {
                top: `${top}px`,
                left: `${Math.max(this.metaTagActionMenu.left - 208, 12)}px`,
            };
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        window.addEventListener('resize', this.closeMetaTagActionMenu);
        window.addEventListener('scroll', this.closeMetaTagActionMenu, true);
        this.loadOptions().finally(() => {
            if (this.isEdit) {
                this.getPage();
            } else {
                this.syncTranslations();
            }
        });
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
        window.removeEventListener('resize', this.closeMetaTagActionMenu);
        window.removeEventListener('scroll', this.closeMetaTagActionMenu, true);
    },
    methods: {
        closeHeaderActionMenu() {
            this.headerActionMenuOpen = false;
        },
        toggleHeaderActionMenu() {
            this.headerActionMenuOpen = !this.headerActionMenuOpen;
        },
        runHeaderAction(action) {
            this.closeHeaderActionMenu();

            if (action === 'publish') {
                this.publishPage();
                return;
            }

            if (action === 'reset') {
                this.resetPageDraft();
                return;
            }

            if (action === 'preview') {
                this.previewPage();
                return;
            }

            if (action === 'cancel') {
                this.$router.push('/workspace/cms/pages');
                return;
            }

            if (action === 'delete') {
                this.deletePage();
            }
        },
        applySlotReorder(payload) {
            const slot = String(payload?.slot || '');
            const items = Array.isArray(payload?.items) ? payload.items : null;

            if (slot === '' || !items) {
                return;
            }

            const slotGroup = this.page.slot_groups.find((group) => group.slot === slot);
            if (!slotGroup) {
                return;
            }

            slotGroup.items = items;
        },
        contentStatusDotClass(entity) {
            if (!entity?.is_active) {
                return 'bg-red-500';
            }

            if (!entity?.published_at || entity?.has_unpublished_changes) {
                return 'bg-amber-500';
            }

            return 'bg-green-500';
        },
        contentStatusLabel(entity) {
            if (!entity?.is_active) {
                return this.$t('Deaktivierter Content');
            }

            if (!entity?.published_at || entity?.has_unpublished_changes) {
                return this.$t('Unveröffentlichter Content');
            }

            return this.$t('Veröffentlichter Content');
        },
        loadOptions() {
            return axios.get('api/workspace/admin/cms/pages/options').then((response) => {
                this.templateOptions = Array.isArray(response.data.templates) ? response.data.templates : [];
                this.languageOptions = Array.isArray(response.data.languages) ? response.data.languages : [];
                this.moduleTypeOptions = Array.isArray(response.data.module_types) ? response.data.module_types : [];
                this.defaultLanguageId = response.data.default_language_id || (this.languageOptions[0] ? this.languageOptions[0].id : null);

                if (!this.page.cms_template_id && this.templateOptions.length > 0) {
                    this.page.cms_template_id = this.templateOptions[0].id;
                }

                this.syncTranslations();
            });
        },
        getPage() {
            this.pageStateResolved = false;
            this.loading = true;
            axios.get('api/workspace/admin/cms/pages/' + this.$route.params.id).then((response) => {
                this.page = response.data;
                if (!Array.isArray(this.page.translations)) {
                    this.page.translations = [];
                }
                this.page.translations = this.page.translations.map((translation) => ({
                    ...translation,
                    content_html: this.formatHtmlForEditor(translation.content_html),
                    published_content_html: this.formatHtmlForEditor(translation.published_content_html),
                    meta_tags: Array.isArray(translation.meta_tags) ? translation.meta_tags : [],
                }));
                this.syncTranslations();
                this.activeTranslationIndex = 0;
                this.pageStateResolved = true;
                this.loading = false;
            }).catch(() => {
                this.pageStateResolved = true;
                this.loading = false;
            });
        },
        savePage() {
            return this.persistPage();
        },
        publishPage() {
            this.persistPage({publishAfterSave: true});
        },
        resetPageDraft() {
            this.openConfirmDialog({
                title: this.$t('Auf veröffentlichte Version zurücksetzen'),
                text: this.$t('Soll der aktuelle Entwurf wirklich auf die zuletzt veröffentlichte Version zurückgesetzt werden? Nicht veröffentlichte Änderungen gehen dabei verloren.'),
                pendingAction: () => {
                    axios.post('api/workspace/admin/cms/pages/' + this.$route.params.id + '/reset').then((response) => {
                        this.page = response.data.page;
                        if (!Array.isArray(this.page.translations)) {
                            this.page.translations = [];
                        }
                        this.page.translations = this.page.translations.map((translation) => ({
                            ...translation,
                            content_html: this.formatHtmlForEditor(translation.content_html),
                            published_content_html: this.formatHtmlForEditor(translation.published_content_html),
                            meta_tags: Array.isArray(translation.meta_tags) ? translation.meta_tags : [],
                        }));
                        this.syncTranslations();
                        this.$notify({title: this.$t('Success'), text: this.$t('Entwurf auf veröffentlichte Version zurückgesetzt'), type: 'success'});
                    });
                },
            });
        },
        previewPage() {
            const previewWindow = window.open('about:blank', '_blank', 'noopener');
            this.openPreview(previewWindow);
        },
        deletePage() {
            this.openConfirmDialog({
                title: this.$t('Seite löschen'),
                text: this.$t('Soll diese Seite wirklich gelöscht werden?'),
                pendingAction: () => {
                    axios.delete('api/workspace/admin/cms/pages/' + this.$route.params.id).then(() => {
                        this.$notify({title: this.$t('Success'), text: this.$t('Data deleted successfully'), type: 'success'});
                        this.$router.push('/workspace/cms/pages');
                    });
                },
            });
        },
        persistPage({publishAfterSave = false} = {}) {
            const request = this.isEdit
                ? axios.put('api/workspace/admin/cms/pages/' + this.$route.params.id, this.page)
                : axios.post('api/workspace/admin/cms/pages', this.page);

            return request.then((response) => {
                const pageId = response.data.page.id;

                if (!this.isEdit) {
                    this.$router.push('/workspace/cms/pages/' + pageId + '/edit');
                }

                this.page = response.data.page;
                if (Array.isArray(this.page.translations)) {
                    this.page.translations = this.page.translations.map((translation) => ({
                        ...translation,
                        content_html: this.formatHtmlForEditor(translation.content_html),
                        published_content_html: this.formatHtmlForEditor(translation.published_content_html),
                    }));
                }
                this.syncTranslations();
                this.activeTranslationIndex = Math.min(this.activeTranslationIndex, Math.max(this.page.translations.length - 1, 0));
                this.pageStateResolved = true;

                if (publishAfterSave) {
                    return axios.post('api/workspace/admin/cms/pages/' + pageId + '/publish').then((publishResponse) => {
                        this.page = publishResponse.data.page;
                        if (Array.isArray(this.page.translations)) {
                            this.page.translations = this.page.translations.map((translation) => ({
                                ...translation,
                                content_html: this.formatHtmlForEditor(translation.content_html),
                                published_content_html: this.formatHtmlForEditor(translation.published_content_html),
                                meta_tags: Array.isArray(translation.meta_tags) ? translation.meta_tags : [],
                            }));
                        }
                        this.syncTranslations();
                        this.activeTranslationIndex = Math.min(this.activeTranslationIndex, Math.max(this.page.translations.length - 1, 0));
                        this.pageStateResolved = true;
                        this.$notify({title: this.$t('Success'), text: this.$t('Seite erfolgreich veröffentlicht'), type: 'success'});

                        return publishResponse;
                    });
                }

                this.$notify({title: this.$t('Success'), text: this.isEdit ? this.$t('Entwurf gespeichert') : this.$t('Entwurf angelegt'), type: 'success'});

                return response;
            });
        },
        openPreview(previewWindow = null) {
            const languageId = this.activeTranslation ? this.activeTranslation.cms_language_id : this.defaultLanguageId;
            const payload = _.cloneDeep(this.page);

            return axios.post('api/workspace/admin/cms/pages/preview', payload, {
                params: languageId ? {language_id: languageId} : {},
            }).then((response) => {
                const previewUrl = response.data.url;

                if (previewWindow) {
                    previewWindow.location.replace(previewUrl);
                } else {
                    window.open(previewUrl, '_blank', 'noopener');
                }

                this.$notify({title: this.$t('Success'), text: this.$t('Vorschau geöffnet'), type: 'success'});

                return response;
            }).catch((error) => {
                if (previewWindow && previewWindow.location.href === 'about:blank') {
                    previewWindow.close();
                }

                throw error;
            });
        },
        openContentHtmlMediaDrawer() {
            this.$refs.pageContentHtmlEditor?.openMediaDrawer();
        },
        translationLabel(translation, index) {
            const language = this.resolveLanguage(translation);
            return language ? language.locale : `${this.$t('Sprache')} ${index + 1}`;
        },
        resolveLanguage(translation) {
            if (translation && translation.language && translation.language.id) {
                return translation.language;
            }

            return this.languageOptions.find((item) => item.id === translation.cms_language_id) || null;
        },
        formatHtmlForEditor(value) {
            const normalized = String(value || '').replace(/\r\n/g, '\n').trim();
            if (normalized === '') {
                return '';
            }

            const tokens = normalized
                .replace(/>\s*</g, '>\n<')
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => line !== '');

            let indentLevel = 0;

            return tokens.map((token) => {
                const isClosingTag = /^<\//.test(token);
                const isComment = /^<!--/.test(token);
                const isDoctype = /^<!DOCTYPE/i.test(token);
                const isOpeningTag = /^<[^/!][^>]*>$/.test(token);
                const isSelfClosingTag = /\/>$/.test(token);
                const opensAndClosesInline = /^<([a-zA-Z0-9:-]+)(\s[^>]*)?>.*<\/\1>$/.test(token);

                if (isClosingTag) {
                    indentLevel = Math.max(indentLevel - 1, 0);
                }

                const formatted = `${'    '.repeat(indentLevel)}${token}`;

                if (!isClosingTag && !isComment && !isDoctype && isOpeningTag && !isSelfClosingTag && !opensAndClosesInline) {
                    indentLevel += 1;
                }

                return formatted;
            }).join('\n');
        },
        syncTranslations() {
            if (!Array.isArray(this.languageOptions) || this.languageOptions.length < 1) {
                this.page.translations = [];
                this.activeTranslationIndex = 0;
                return;
            }

            const existingByLanguageId = new Map(
                (Array.isArray(this.page.translations) ? this.page.translations : []).map((translation) => [
                    translation.cms_language_id,
                    translation,
                ])
            );

            this.page.translations = this.languageOptions.map((language) => {
                const existing = existingByLanguageId.get(language.id);

                return {
                    id: existing?.id || null,
                    cms_language_id: language.id,
                    title: existing?.title || '',
                    seo_title: existing?.seo_title || '',
                    seo_description: existing?.seo_description || '',
                    seo_canonical_url: existing?.seo_canonical_url || '',
                    seo_robots: existing?.seo_robots || 'index,follow',
                    content_html: this.formatHtmlForEditor(existing?.content_html || ''),
                    published_title: existing?.published_title || '',
                    published_seo_title: existing?.published_seo_title || '',
                    published_seo_description: existing?.published_seo_description || '',
                    published_seo_canonical_url: existing?.published_seo_canonical_url || '',
                    published_seo_robots: existing?.published_seo_robots || 'index,follow',
                    published_content_html: this.formatHtmlForEditor(existing?.published_content_html || ''),
                    is_active: typeof existing?.is_active === 'boolean' ? existing.is_active : !!language.is_active,
                    published_is_active: typeof existing?.published_is_active === 'boolean' ? existing.published_is_active : !!language.is_active,
                    routes_count: existing?.routes_count || 0,
                    meta_tags: Array.isArray(existing?.meta_tags)
                        ? existing.meta_tags.map((metaTag) => ({
                            attribute_type: metaTag.attribute_type || 'name',
                            attribute_key: metaTag.attribute_key || '',
                            content: metaTag.content || '',
                            is_active: typeof metaTag.is_active === 'boolean' ? metaTag.is_active : true,
                            sort_order: Number.isInteger(metaTag.sort_order) ? metaTag.sort_order : 0,
                        }))
                        : [],
                    language: {
                        id: language.id,
                        locale: language.locale,
                        name: language.name,
                        is_default: !!language.is_default,
                    },
                };
            });

            this.activeTranslationIndex = Math.max(0, Math.min(this.activeTranslationIndex, this.page.translations.length - 1));
        },
        addMetaTag() {
            if (!this.activeTranslation) {
                return;
            }

            this.closeMetaTagActionMenu();
            this.activeTranslation.meta_tags.push({
                attribute_type: 'name',
                attribute_key: '',
                content: '',
                is_active: true,
                sort_order: this.activeTranslation.meta_tags.length,
            });
        },
        toggleMetaTagActionMenu(index, metaTag, event) {
            if (this.metaTagActionMenu.index === index) {
                this.closeMetaTagActionMenu();
                return;
            }

            const rect = event.currentTarget.getBoundingClientRect();
            const estimatedHeight = 52;
            const hasSpaceBelow = (window.innerHeight - rect.bottom) >= (estimatedHeight + 16);

            this.metaTagActionMenu = {
                index,
                metaTag,
                top: hasSpaceBelow ? rect.bottom : rect.top,
                left: rect.right,
                openUpward: !hasSpaceBelow,
            };
        },
        closeMetaTagActionMenu() {
            this.metaTagActionMenu = {
                index: null,
                top: 0,
                left: 0,
                openUpward: false,
            };
        },
        handleDocumentClick(event) {
            if (this.headerActionMenuOpen) {
                const menuElement = this.$refs.headerActionMenu;
                const triggerElement = this.$refs.headerActionMenuTrigger;
                const wrapperElement = this.$refs.headerActionMenuWrapper;

                const clickedInsideMenu = menuElement && menuElement.contains(event.target);
                const clickedTrigger = triggerElement && triggerElement.contains(event.target);
                const clickedWrapper = wrapperElement && wrapperElement.contains(event.target);

                if (!clickedInsideMenu && !clickedTrigger && !clickedWrapper) {
                    this.closeHeaderActionMenu();
                }
            }

            if (this.metaTagActionMenu.index === null) {
                return;
            }

            const menuElement = this.$refs.metaTagActionMenu;
            const triggerSelector = `[data-page-meta-tag-action-trigger="${this.metaTagActionMenu.index}"]`;
            const triggerElement = event.target.closest(triggerSelector);

            if (!(menuElement && menuElement.contains(event.target)) && !triggerElement) {
                this.closeMetaTagActionMenu();
            }
        },
        removeMetaTag(index) {
            this.closeMetaTagActionMenu();
            this.openConfirmDialog({
                title: this.$t('Metatag entfernen'),
                text: this.$t('Soll dieses individuelle Metatag wirklich entfernt werden?'),
                pendingAction: () => {
                    if (!this.activeTranslation) {
                        return;
                    }

                    this.activeTranslation.meta_tags.splice(index, 1);
                },
            });
        },
        removeActionMenuMetaTag() {
            if (this.metaTagActionMenu.index === null) {
                return;
            }

            this.removeMetaTag(this.metaTagActionMenu.index);
        },
        openConfirmDialog({title, text, pendingAction}) {
            this.confirmDialog = {
                open: true,
                title,
                text,
                pendingAction,
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
    },
}
</script>

<style scoped>
.font-base {
    font-weight: 400;
}

.cms-translation-content {
    padding: 0;
}

.cms-translation-meta {
    padding: 1.25rem;
}

.cms-translation-code-head {
    padding: 0.875rem 1.25rem 0.75rem;
    border-top: 1px solid rgb(243, 244, 246);
    border-bottom: 1px solid rgb(243, 244, 246);
    background: #ffffff;
    color: rgb(107, 114, 128);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.cms-translation-html-editor {
    height: 28rem;
    min-height: 28rem;
    padding: 0 !important;
    overflow: hidden;
}

.cms-meta-tag-table :deep(.table-listview-l-td),
.cms-meta-tag-table :deep(.table-listview-l-th) {
    vertical-align: middle;
}

.cms-translation-accordion {
    border-top: 1px solid var(--ui-border-subtle);
    border-bottom: 1px solid var(--ui-border-subtle);
    background: #ffffff;
}

.cms-translation-accordion-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.5rem;
    background: var(--ui-surface-raised);
    color: var(--ui-text-muted);
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 500;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    border: 0;
}

.cms-translation-accordion-body {
    background: #ffffff;
    border-top: 1px solid var(--ui-border-subtle);
}

.cms-meta-tag-table {
    margin: 0;
}

.cms-translation-code-head--flush-top {
    border-top: 0;
}

.cms-meta-tag-table :deep(thead th:first-child),
.cms-meta-tag-table :deep(tbody td:first-child) {
    padding-left: 1.25rem;
}

.cms-meta-tag-table :deep(thead th:last-child),
.cms-meta-tag-table :deep(tbody td:last-child) {
    padding-right: 1.25rem;
}

</style>
